import type {
  AnalyticsRange,
  AnalyticsTab,
  StaffAnalyticsEventDetail,
  StaffAnalyticsEventPage,
  StaffAnalyticsEventsQuery,
  StaffAnalyticsRecentVisits,
  StaffAnalyticsVisitDetail,
} from "@lafayette/schemas";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import type { CustomerView } from "../queries";
import { useAnalyticsRuntime } from "../runtime";

// The reads of the Events tab and of the "recently" card (CHT-2030). Like the other reads of the area: nothing stays in the cache after the page
// lets go (`gcTime: 0`), nothing is retried by the library, and every read runs inside the audited opening of the view (`view.read`), which opens
// again once and retries once when the API refuses it. A vendor failure is NOT an error here: it is `availability` inside the answer.

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export type EventFilters = Readonly<{
  area: StaffAnalyticsEventsQuery["area"];
  event: StaffAnalyticsEventsQuery["event"];
  visitId: string | null;
  /** The time window inside the range (both ends), or `null` for the whole range. The server keeps it inside the 13 months. */
  window: Readonly<{ from: string; to: string }> | null;
}>;

const windowQuery = (window: EventFilters["window"]) =>
  window === null ? {} : { from: window.from, to: window.to };

const PAGE_SIZE = 50;

/** The pages of the event table, newest first. The cursor of the last page opens the next one ("Load older events"). */
export function useEventPages(
  view: CustomerView,
  customerId: string,
  range: AnalyticsRange,
  filters: EventFilters,
) {
  const { events, timeZone } = useAnalyticsRuntime();
  const tz = timeZone();
  return useInfiniteQuery<StaffAnalyticsEventPage>({
    queryKey: [
      "analytics-events",
      customerId,
      range,
      tz,
      filters.area,
      filters.event,
      filters.visitId,
      filters.window?.from ?? null,
      filters.window?.to ?? null,
    ],
    queryFn: ({ pageParam, signal }) =>
      view.read((openingId) =>
        events.events(
          customerId,
          openingId,
          {
            range,
            tz,
            limit: PAGE_SIZE,
            ...windowQuery(filters.window),
            ...(filters.area === undefined ? {} : { area: filters.area }),
            ...(filters.event === undefined ? {} : { event: filters.event }),
            ...(filters.visitId === null ? {} : { visitId: filters.visitId }),
            ...(typeof pageParam === "string" ? { cursor: pageParam } : {}),
          },
          { signal },
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: view.ready,
    ...READ_OPTIONS,
  });
}

/** How many events there are in the window: the count of the tab label. */
export function useEventCount(
  view: CustomerView,
  customerId: string,
  range: AnalyticsRange,
  enabled = true,
  window: EventFilters["window"] = null,
) {
  const { events, timeZone } = useAnalyticsRuntime();
  const tz = timeZone();
  return useQuery({
    queryKey: [
      "analytics-event-count",
      customerId,
      range,
      tz,
      window?.from ?? null,
      window?.to ?? null,
    ],
    queryFn: ({ signal }) =>
      view.read((openingId) =>
        events.eventSummary(
          customerId,
          openingId,
          { range, tz, ...windowQuery(window) },
          { signal },
        ),
      ),
    enabled: enabled && view.ready,
    ...READ_OPTIONS,
  });
}

export function useEventDetail(view: CustomerView, customerId: string, eventUuid: string | null) {
  const { events } = useAnalyticsRuntime();
  return useQuery<StaffAnalyticsEventDetail>({
    queryKey: ["analytics-event", customerId, eventUuid],
    queryFn: ({ signal }) =>
      view.read((openingId) => events.event(customerId, openingId, eventUuid ?? "", { signal })),
    enabled: view.ready && eventUuid !== null,
    ...READ_OPTIONS,
  });
}

export function useVisitSteps(view: CustomerView, customerId: string, visitId: string | null) {
  const { events } = useAnalyticsRuntime();
  return useQuery<StaffAnalyticsVisitDetail>({
    queryKey: ["analytics-visit", customerId, visitId],
    queryFn: ({ signal }) =>
      view.read((openingId) => events.visit(customerId, openingId, visitId ?? "", { signal })),
    enabled: view.ready && visitId !== null,
    ...READ_OPTIONS,
  });
}

/** The last visits with their first steps, for the card of the Overview. It runs inside the opening of the Overview tab (the server accepts it). */
export function useRecentVisits(
  view: CustomerView,
  customerId: string,
  range: AnalyticsRange,
  enabled = true,
) {
  const { events, timeZone } = useAnalyticsRuntime();
  const tz = timeZone();
  return useQuery<StaffAnalyticsRecentVisits>({
    queryKey: ["analytics-recent-visits", customerId, range, tz],
    queryFn: ({ signal }) =>
      view.read((openingId) =>
        events.recentVisits(customerId, openingId, { range, tz }, { signal }),
      ),
    enabled: enabled && view.ready,
    ...READ_OPTIONS,
  });
}

/**
 * The count of the label of the Events tab: the summary on the Events tab, the recent-visits answer on the Overview (the card reads the same query
 * and shares its answer). `null`: not known, or app data is not available for this customer.
 */
export function useEventCountLabel(
  view: CustomerView,
  customerId: string,
  tab: AnalyticsTab,
  range: AnalyticsRange,
): number | null {
  const summary = useEventCount(view, customerId, range, tab === "events");
  const recent = useRecentVisits(view, customerId, range, tab === "overview");
  if (tab === "events") return summary.data?.count ?? null;
  return tab === "overview" ? (recent.data?.eventCount ?? null) : null;
}
